import { useEffect, useRef, useState } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronDown, CircleUserRound, ExternalLink } from 'lucide-react';
import { NavLink, Outlet } from 'react-router';

import { getKuduUserId } from '@/api/legacyKuduAuth';
import { getAccountEmail } from '@/auth/accountEmail';
import { useAuth } from '@/auth/useAuth';
import { Button } from '@/components/Button/Button';
import { NavigationPanelStateProvider } from '@/features/content/NavigationPanelState';
import { profileQueryKey, type ProfileData } from '@/features/profile/profileTypes';
import { indexPath } from '@/features/support/supportContent';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import kuduLogoUrl from '@/images/kudu-logo-full.svg';

import styles from './AppShell.module.css';

/**
 * Help opens in a new tab, because nothing guards a typed answer that has not
 * been saved: leaving the page in this tab would lose it.
 */
function HelpMenuItem() {
  const { user } = useAuth();
  // What the route above the shell has already read, so this asks the server
  // nothing. The support console's route reads no profile: without one, Help
  // opens the student help, which links to the instructor help.
  const profile = useQueryClient().getQueryData<ProfileData>(
    profileQueryKey(getKuduUserId(user) ?? user?.uid ?? null),
  );

  return (
    <DropdownMenu.Item asChild>
      <a
        aria-label="Help, opens in a new tab"
        className={styles.accountMenuItem}
        href={indexPath(profile?.isInstructor ? 'instructor' : 'student')}
        rel="noreferrer"
        target="_blank"
      >
        Help
        <ExternalLink aria-hidden="true" size={14} />
      </a>
    </DropdownMenu.Item>
  );
}

export function AppShell() {
  const { signOut, user } = useAuth();
  // The product app uses the default brand title; named titles are marketing-only.
  useDocumentTitle();
  const [isSigningOut, setIsSigningOut] = useState(false);
  const headerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }

    const headerElement = headerRef.current;

    if (!headerElement) {
      return undefined;
    }

    const rootStyle = document.documentElement.style;
    const updateHeaderHeight = () => {
      const measuredHeight = `${Math.ceil(
        headerElement.getBoundingClientRect().height,
      )}px`;
      rootStyle.setProperty('--header-height', measuredHeight);
    };

    updateHeaderHeight();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateHeaderHeight);
      return () => {
        window.removeEventListener('resize', updateHeaderHeight);
      };
    }

    const observer = new ResizeObserver(() => {
      updateHeaderHeight();
    });

    observer.observe(headerElement);

    return () => {
      observer.disconnect();
    };
  }, []);

  async function handleSignOut() {
    setIsSigningOut(true);

    try {
      await signOut();
    } finally {
      setIsSigningOut(false);
    }
  }

  return (
    <div className={styles.shell}>
      <header
        className={styles.header}
        data-app-shell-header="true"
        ref={headerRef}
      >
        <div className={styles.headerInner}>
          <NavLink
            aria-label="Kudu home"
            className={styles.brand}
            to="/app/courses"
          >
            <img alt="Kudu" className={styles.brandLogo} src={kuduLogoUrl} />
          </NavLink>
          <nav aria-label="Primary navigation" className={styles.nav}>
            <NavLink
              className={({ isActive }) =>
                isActive ? `${styles.link} ${styles.linkActive}` : styles.link
              }
              to="/app/courses"
            >
              Courses
            </NavLink>

            <DropdownMenu.Root modal={false}>
              <DropdownMenu.Trigger asChild>
                <Button
                  aria-label="Open account menu"
                  className={styles.accountTrigger}
                  size="small"
                  variant="ghost"
                >
                  <CircleUserRound aria-hidden="true" size={18} />
                  <ChevronDown aria-hidden="true" size={16} />
                </Button>
              </DropdownMenu.Trigger>

              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  align="end"
                  className={styles.accountMenu}
                  sideOffset={8}
                >
                  <DropdownMenu.Label className={styles.accountLabel}>
                    <span className={styles.accountKicker}>Signed in as</span>
                    <span className={styles.accountEmail}>
                      {getAccountEmail(user) || 'Authenticated user'}
                    </span>
                  </DropdownMenu.Label>
                  <DropdownMenu.Item asChild>
                    <NavLink className={styles.accountMenuItem} to="/app/profile">
                      Profile
                    </NavLink>
                  </DropdownMenu.Item>
                  <HelpMenuItem />
                  <DropdownMenu.Separator className={styles.accountSeparator} />
                  <DropdownMenu.Item
                    className={styles.accountMenuItem}
                    disabled={isSigningOut}
                    onSelect={() => {
                      void handleSignOut();
                    }}
                  >
                    {isSigningOut ? 'Signing out...' : 'Sign out'}
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          </nav>
        </div>
      </header>
      <main className={styles.main}>
        <NavigationPanelStateProvider>
          <Outlet />
        </NavigationPanelStateProvider>
      </main>
    </div>
  );
}
